import React, { useState, useEffect, useCallback, memo } from "react";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from "axios";
import { Box, Button, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import "./AddTimetablePopup.css";
import { useAuth } from "../../AuthContext.js";
import configData from '../../configure';

const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const sortSchedulesByTime = (scheduleArray) => {
  if (!scheduleArray) return [];
  return [...scheduleArray].sort((a, b) => {
    const timeA = a.startTime.split(":").map(Number);
    const timeB = b.startTime.split(":").map(Number);
    return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1]);
  });
};

const CourseSelect = memo(({ form, handleInputChange, assignedCourses }) => {
  // Ensure unique courses to prevent duplicate MenuItem rendering
  const uniqueCourses = Array.from(new Set(assignedCourses.map(course => course.courseId)))
    .map(courseId => assignedCourses.find(course => course.courseId === courseId));
  console.log("Rendering CourseSelect with form.courseId:", form.courseId);
  return (
    <FormControl fullWidth className="cl-form-select">
      <InputLabel>Course<span className="cl-required">*</span></InputLabel>
      <Select
        key={`course-select-${form.courseId}`}
        name="courseId"
        value={form.courseId || ""}
        onChange={handleInputChange}
        label="Course *"
      >
        <MenuItem value="">
          <em>Select a course</em>
        </MenuItem>
        {uniqueCourses.length > 0 ? (
          uniqueCourses.map((course) => {
            console.log("MenuItem courseId:", course.courseId, "Name:", course.name);
            return (
              <MenuItem key={course.courseId} value={String(course.courseId)}>
                {course.code} - {course.name}
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>No courses assigned</MenuItem>
        )}
      </Select>
    </FormControl>
  );
});

const AddScheduleModal = ({ isOpen, onClose, onSave, classInfo, isEditMode = false, assignedCourses = [], orgId }) => {
  const [schedules, setSchedules] = useState({});
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [editingDay, setEditingDay] = useState(null);
  const [faculties, setFaculties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { axios, token } = useAuth();
  const [selectedSchedules, setSelectedSchedules] = useState(new Set());
const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
const getScheduleId = (day, idx) => `${day}-${idx}`;

const handleScheduleSelect = (day, idx) => {
  const scheduleId = getScheduleId(day, idx);
  setSelectedSchedules(prev => {
    const newSet = new Set(prev);
    if (newSet.has(scheduleId)) {
      newSet.delete(scheduleId);
    } else {
      newSet.add(scheduleId);
    }
    return newSet;
  });
};
  const [form, setForm] = useState({
    days: [],
    courseId: "",
    facultyId: "",
    startTime: "",
    durationMins: "",
    location: ""
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Log form.courseId changes
  useEffect(() => {
    console.log("form.courseId updated:", form.courseId);
  }, [form.courseId]);

  const fetchFaculties = useCallback(async () => {
    if (!orgId) {
      setError("Organization ID is missing.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${configData.GET_USERS_BY_TYPE}/${orgId}`,
        {
          params: {
            userType: "FACULTY",
            pageSize: 500,
          },
          headers: {
            accept: "application/hal+json",
            Authorization: token,
          },
        }
      );

      const rawItems = response.data?.items || response.data?.content || response.data || [];
      const items = Array.isArray(rawItems) ? rawItems : [];

      if (items.length > 0) {
        const extractedFaculties = items.map((item) => ({
          ...item.user,
          rawData: item,
          city: item.user?.addressDetails?.[0]?.city || "N/A",
          dateOfBirth: item.user?.dateOfBirth || "N/A",
          employeeId: item.facultyInfo?.employeeId || "N/A",
          designation: item.facultyInfo?.designation || "N/A",
        }));
        setFaculties(extractedFaculties);
        setError(null);
      } else {
        setFaculties([]);
        setError("No faculty data returned from the server.");
      }
    } catch (error) {
      console.error("Error fetching faculties:", error);
      setFaculties([]);
      setError("Failed to load faculty list. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [orgId, token, axios]);

  useEffect(() => {
    if (isOpen) {
      fetchFaculties();
      console.log("Assigned Courses:", assignedCourses);
    }
  }, [isOpen, fetchFaculties, assignedCourses]);

  useEffect(() => {
    if (isEditMode && classInfo?.classScheduleMap) {
      const sortedMap = {};
      Object.entries(classInfo.classScheduleMap).forEach(([day, slots]) => {
        sortedMap[day] = sortSchedulesByTime(slots);
      });
      setSchedules(sortedMap);
    } else {
      setSchedules({});
    }
  }, [isEditMode, classInfo]);

  const [classStudents, setClassStudents] = useState([]);

  useEffect(() => {
    const fetchClassStudents = async () => {
      const cId = classInfo?.classId || classInfo?.id;
      if (!cId || !isOpen) return;
      try {
        const response = await axios.get(`${configData.GET_STUDENTS_BY_CLASS}/${cId}`, {
          params: { pageSize: 200 },
          headers: { Authorization: token }
        });
        const rawItems = response.data?.items || response.data?.content || response.data || [];
        const items = Array.isArray(rawItems) ? rawItems : [];
        setClassStudents(items);
      } catch (err) {
        console.error("Error fetching class students for schedule:", err);
        setClassStudents([]);
      }
    };
    fetchClassStudents();
  }, [isOpen, classInfo, axios, token]);

  useEffect(() => {
    if (!isOpen) {
      setForm({
        days: [],
        courseId: "",
        facultyId: "",
        studentId: "ALL",
        startTime: "",
        durationMins: "",
        location: ""
      });
      setEditingScheduleId(null);
      setEditingDay(null);
      setDropdownOpen(false);
      setFaculties([]);
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    console.log("Input Change - Name:", e.target.name, "Value:", e.target.value);
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleDayChange = (day) => {
    setForm(prev => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day]
    }));
  };

  const handleSelectAllDays = () => {
  setForm(prev => ({
    ...prev,
    days: prev.days.length === weekdays.length ? [] : [...weekdays]
  }));
};

  const handleAddSchedule = () => {
    if (!form.courseId || !form.startTime || !form.durationMins) {
      toast.warning("Please fill in all required fields (Course, Start Time, Duration)");
      return;
    }

    if (form.days.length === 0) {
      toast.warning("Please select at least one day");
      return;
    }

    const selectedCourse = assignedCourses.find((course) => course.courseId === form.courseId);
    const selectedFaculty = faculties.find((faculty) => faculty.userId === form.facultyId);
    const selectedStudent = classStudents.find((st) => String(st.userId || st.id || st.user?.userId) === String(form.studentId));

    const courseName = selectedCourse ? selectedCourse.name : "";
    const facultyName = selectedFaculty
      ? `${selectedFaculty.firstName} ${selectedFaculty.lastName} (${selectedFaculty.employeeId} - ${selectedFaculty.designation})`
      : "";
    const studentName = selectedStudent
      ? (selectedStudent.displayName || `${selectedStudent.firstName || ''} ${selectedStudent.lastName || ''}`.trim() || 'Student')
      : (!form.studentId || form.studentId === "ALL" ? "All Class Students" : "Student");
    const studentAdmissionId = selectedStudent
      ? (selectedStudent.admissionId || selectedStudent.studentInfo?.admissionId || selectedStudent.raw?.studentInfo?.admissionId || 'N/A')
      : "ALL";

    setSchedules((prev) => {
      const updated = { ...prev };
      if (editingScheduleId !== null && editingDay !== null) {
        if (updated[editingDay] && updated[editingDay][editingScheduleId]) {
          updated[editingDay].splice(editingScheduleId, 1);
          if (updated[editingDay].length === 0) {
            delete updated[editingDay];
          }
        }
      }

      form.days.forEach(day => {
        if (!updated[day]) updated[day] = [];
        updated[day].push({
          courseId: form.courseId,
          facultyId: form.facultyId,
          studentId: form.studentId || "ALL",
          studentName,
          studentAdmissionId,
          startTime: form.startTime,
          durationMins: form.durationMins,
          location: form.location,
          courseName,
          facultyName,
          isRescheduled: false,
          rescheduledDate: null,
          rescheduledTime: null,
          rescheduledReason: ""
        });
        updated[day] = sortSchedulesByTime(updated[day]);
      });

      return updated;
    });

    setForm({
      days: [],
      courseId: "",
      facultyId: "",
      studentId: "ALL",
      startTime: "",
      durationMins: "",
      location: ""
    });
    setEditingScheduleId(null);
    setEditingDay(null);
  };


  const handleSelectAllSchedules = () => {
  if (selectedSchedules.size === getTotalScheduleCount()) {
    setSelectedSchedules(new Set());
  } else {
    const allScheduleIds = new Set();
    Object.entries(schedules).forEach(([day, slots]) => {
      slots.forEach((_, idx) => {
        allScheduleIds.add(getScheduleId(day, idx));
      });
    });
    setSelectedSchedules(allScheduleIds);
  }
};

const getTotalScheduleCount = () => {
  return Object.values(schedules).reduce((total, slots) => total + slots.length, 0);
};

const handleDeleteSelected = () => {
  if (selectedSchedules.size === 0) return;
  
  if (window.confirm(`Are you sure you want to delete ${selectedSchedules.size} selected schedule(s)?`)) {
    setSchedules(prev => {
      const updated = { ...prev };
      
      // Group selected schedules by day
      const schedulesToDelete = {};
      selectedSchedules.forEach(scheduleId => {
        const [day, idx] = scheduleId.split('-');
        if (!schedulesToDelete[day]) {
          schedulesToDelete[day] = [];
        }
        schedulesToDelete[day].push(parseInt(idx));
      });
      
      // Delete schedules (in reverse order to maintain indices)
      Object.entries(schedulesToDelete).forEach(([day, indices]) => {
        const sortedIndices = indices.sort((a, b) => b - a);
        sortedIndices.forEach(idx => {
          updated[day].splice(idx, 1);
        });
        
        if (updated[day].length === 0) {
          delete updated[day];
        }
      });
      
      return updated;
    });
    
    setSelectedSchedules(new Set());
    setIsMultiSelectMode(false);
  }
};
 const handleEditSchedule = (day, scheduleIndex) => {
  const scheduleToEdit = schedules[day][scheduleIndex];
  console.log("Editing Schedule:", scheduleToEdit);
  const validCourse = assignedCourses.find(course => course.courseId === scheduleToEdit.courseId);
  if (!validCourse) {
    console.error(`Course ID ${scheduleToEdit.courseId} not found in assignedCourses`);
    toast.error("The selected course is no longer available.");
    setForm({
      days: [day],
      courseId: "",
      facultyId: scheduleToEdit.facultyId,
      startTime: scheduleToEdit.startTime,
      durationMins: scheduleToEdit.durationMins,
      location: scheduleToEdit.location,
    });
  } else {
    setForm({
      days: [day],
      courseId: String(scheduleToEdit.courseId),
      facultyId: scheduleToEdit.facultyId,
      startTime: scheduleToEdit.startTime,
      durationMins: scheduleToEdit.durationMins,
      location: scheduleToEdit.location,
    });
  }
  setEditingScheduleId(scheduleIndex);
  setEditingDay(day);
};

const handleDeleteSchedule = (day, scheduleIndex) => {
  if (window.confirm("Are you sure you want to delete this schedule?")) {
    setSchedules((prev) => {
      const updated = { ...prev };
      updated[day].splice(scheduleIndex, 1);
      if (updated[day].length === 0) {
        delete updated[day];
      }
      return updated;
    });
  }
};

  const handleCancelEdit = () => {
    setForm({
      days: [],
      courseId: "",
      facultyId: "",
      startTime: "",
      durationMins: "",
      location: ""
    });
    setEditingScheduleId(null);
    setEditingDay(null);
    setDropdownOpen(false);
  };

  const handleSubmit = () => {
    if (Object.keys(schedules).length === 0) {
      toast.warning("Please add at least one schedule before saving.");
      return;
    }

    const payload = {
      ...classInfo,
      classScheduleMap: schedules,
    };
    onSave(payload);
  };



  if (!isOpen) return null;

  return (
    <div className="cl-modal-overlay">
      <div className="cl-modal cl-enhanced-modal">
        <div className="cl-modal-header">
          <h2>{isEditMode ? "Edit Class Schedule" : "Add Class Schedule"}</h2>
          <button className="cl-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="cl-modal-content">
          <div className="cl-form-section">
            <h3>{editingScheduleId !== null ? "Edit Schedule Item" : "Add New Schedule"}</h3>
            {error && (
              <div style={{ color: "#e74c3c", marginBottom: "16px", fontSize: "14px" }}>
                {error}
              </div>
            )}
            {loading && (
              <div style={{ color: "#34495e", marginBottom: "16px", fontSize: "14px" }}>
                Loading faculty list...
              </div>
            )}

            <div className="cl-form-group-row">
              <label className="cl-form-label">
                Days:<span className="cl-required">*</span>
              </label>
              <div className="cl-dropdown-container">
                <div
                  className="cl-dropdown-header"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <span className="cl-dropdown-text">
                    {form.days.length === 0
                      ? "Select days"
                      : form.days.length === 1
                      ? form.days[0]
                      : `${form.days.length} days selected`}
                  </span>
                  <span className={`cl-dropdown-arrow ${dropdownOpen ? "open" : ""}`}>▼</span>
                </div>
                {dropdownOpen && (
  <div className="cl-dropdown-content">
    <div className="cl-dropdown-select-all">
      <label className="cl-dropdown-option">
        <input
          type="checkbox"
          checked={form.days.length === weekdays.length}
          onChange={handleSelectAllDays}
        />
        <span className="cl-option-text">
          {form.days.length === weekdays.length ? "Deselect All" : "Select All"}
        </span>
      </label>
    </div>
    <div className="cl-dropdown-options">
      {weekdays.map((day) => (
        <label key={day} className="cl-dropdown-option">
          <input
            type="checkbox"
            checked={form.days.includes(day)}
            onChange={() => handleDayChange(day)}
          />
          <span className="cl-option-text">{day}</span>
        </label>
      ))}
    </div>
  </div>
)}
              </div>
            </div>

            <div className="cl-form-group-row">
              <CourseSelect form={form} handleInputChange={handleInputChange} assignedCourses={assignedCourses} />
            </div>

            <div className="cl-form-group-row">
              <FormControl fullWidth className="cl-form-select">
                <InputLabel>Faculty</InputLabel>
                <Select
                  name="facultyId"
                  value={form.facultyId}
                  onChange={handleInputChange}
                  label="Faculty"
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>Select a faculty</em>
                  </MenuItem>
                  {faculties && faculties.length > 0 ? (
                    faculties.map((faculty) => (
                      <MenuItem key={faculty.userId} value={faculty.userId}>
                        {faculty.firstName} {faculty.lastName} ({faculty.employeeId} - {faculty.designation})
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No faculty available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </div>

            <div className="cl-form-group-row">
              <FormControl fullWidth className="cl-form-select">
                <InputLabel>Scheduled Student (Optional)</InputLabel>
                <Select
                  name="studentId"
                  value={form.studentId || "ALL"}
                  onChange={handleInputChange}
                  label="Scheduled Student (Optional)"
                >
                  <MenuItem value="ALL">
                    <em>All Students (Entire Class)</em>
                  </MenuItem>
                  {classStudents && classStudents.length > 0 ? (
                    classStudents.map((st) => {
                      const stId = String(st.userId || st.id || st.user?.userId);
                      const stName = st.displayName || `${st.firstName || ''} ${st.lastName || ''}`.trim() || 'Student';
                      const admId = st.admissionId || st.studentInfo?.admissionId || st.raw?.studentInfo?.admissionId || 'N/A';
                      return (
                        <MenuItem key={stId} value={stId}>
                          {stName} (Adm: {admId})
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem disabled>No students found in this class</MenuItem>
                  )}
                </Select>
              </FormControl>
            </div>

            {["startTime", "durationMins", "location"].map((field) => (
              <div key={field} className="cl-form-group-row">
                <label className="cl-form-label">
                  {field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, " $1")}:
                  {(field === "startTime" || field === "durationMins") && (
                    <span className="cl-required">*</span>
                  )}
                </label>
                <input
                  type={field === "startTime" ? "time" : field === "durationMins" ? "number" : "text"}
                  name={field}
                  value={form[field]}
                  onChange={handleInputChange}
                  className="cl-form-input"
                  placeholder={field === "durationMins" ? "Duration in minutes" : `Enter ${field}`}
                />
              </div>
            ))}

            <div className="cl-form-actions">
              {editingScheduleId !== null ? (
                <>
                  <button onClick={handleAddSchedule} className="cl-button cl-update-btn">
                    Update Schedule
                  </button>
                  <button onClick={handleCancelEdit} className="cl-button cl-cancel-edit-btn">
                    Cancel Edit
                  </button>
                </>
              ) : (
                <button onClick={handleAddSchedule} className="cl-button cl-add-btn">
                  + Add to Schedule
                </button>
              )}
            </div>
          </div>

      <div className="cl-schedule-preview">
  <div className="cl-schedule-header">
    <h3>Schedule Preview:</h3>
    {Object.keys(schedules).length > 0 && (
      <div className="cl-schedule-controls">
        <button
          onClick={() => {
            setIsMultiSelectMode(!isMultiSelectMode);
            setSelectedSchedules(new Set());
          }}
          className={`cl-multi-select-btn ${isMultiSelectMode ? 'active' : ''}`}
        >
          {isMultiSelectMode ? 'Cancel' : 'Select Multiple'}
        </button>
        
        {isMultiSelectMode && (
          <>
            <button
              onClick={handleSelectAllSchedules}
              className="cl-select-all-schedules-btn"
            >
              {selectedSchedules.size === getTotalScheduleCount() ? 'Deselect All' : 'Select All'}
            </button>
            
            {selectedSchedules.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="cl-delete-selected-btn"
              >
                Delete Selected ({selectedSchedules.size})
              </button>
            )}
          </>
        )}
      </div>
    )}
  </div>
  
  {Object.keys(schedules).length === 0 ? (
    <p className="cl-no-schedule">No schedules added yet.</p>
  ) : (
    <div className="cl-schedule-grid">
      {Object.entries(schedules).map(([day, slots]) => (
        <div key={day} className="cl-day-schedule">
          <h4 className="cl-day-header">{day}</h4>
          <div className="cl-schedule-items">
            {slots.map((slot, idx) => {
              const scheduleId = getScheduleId(day, idx);
              const isSelected = selectedSchedules.has(scheduleId);
              
              return (
                <div 
                  key={idx} 
                  className={`cl-schedule-item-preview ${isSelected ? 'cl-selected' : ''} ${isMultiSelectMode ? 'cl-multi-select-mode' : ''}`}
                >
                  {isMultiSelectMode && (
                    <div className="cl-schedule-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleScheduleSelect(day, idx)}
                      />
                    </div>
                  )}
                  
                  <div className="cl-schedule-item-info">
                    <div className="cl-course-name">{slot.courseName}</div>
                    <div className="cl-schedule-details">
                      <span className="cl-time">{slot.startTime}</span>
                      <span className="cl-duration">{slot.durationMins} mins</span>
                      {slot.location && <span className="cl-location">{slot.location}</span>}
                    </div>
                    {slot.facultyName && (
                      <div className="cl-faculty">Faculty: {slot.facultyName}</div>
                    )}
                    {slot.studentName && (
                      <div style={{ fontSize: '11px', color: '#014486', fontWeight: 600, marginTop: '2px' }}>
                        Student: {slot.studentName} {slot.studentAdmissionId && slot.studentAdmissionId !== 'ALL' ? `(${slot.studentAdmissionId})` : ''}
                      </div>
                    )}
                  </div>
                  
                  <div className="cl-schedule-item-actions">
                    {!isMultiSelectMode && (
                      <>
                        <button
                          onClick={() => handleEditSchedule(day, idx)}
                          className="cl-edit-schedule-btn"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteSchedule(day, idx)}
                          className="cl-delete-schedule-btn"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  )}
</div>
        </div>

        <div className="cl-modal-actions">
          <button onClick={onClose} className="cl-button cl-cancel-btn">Cancel</button>
          <button onClick={handleSubmit} className="cl-button cl-save-btn">
            {isEditMode ? "Update Schedule" : "Save Schedule"}
          </button>
        </div>
      </div>

    </div>
  );
};

export default memo(AddScheduleModal);